/**
 * useSettingsSectionShell — ce que les pages de « Paramètres » partagent, chez l'élève comme chez le
 * tuteur : les mots de l'enregistrement, les pages web du pied, la fiche du store, et la sortie gardée.
 *
 * **La sortie** (le retour de l'en-tête et le bouton retour d'Android, que l'ancien écran écoutait
 * aussi) : d'une page modifiée, la garde « Modifications non enregistrées » ; d'une page, la liste (ou
 * l'écran d'avant si la page a été ouverte directement) ; de la liste, l'écran d'avant (`account` si
 * la pile est vide, comme l'ancien écran). La garde remplace la boîte legacy de
 * `SettingsTabNavigator` : ses boutons gardent leur mesure.
 */
import { useNavigation } from "@react-navigation/native"
import Constants from "expo-constants"
import { useCallback, useEffect, useRef, type Dispatch } from "react"
import { BackHandler, Linking, Platform } from "react-native"

import { type SettingsDrafts, type SettingsUiAction, type SettingsUiState } from "./settingsUiState"
import { trackDialogButton } from "./trackDialogButton"
import { useBackOr } from "./useBackOr"

export const SETTINGS_SAVED_MESSAGE = "Tes paramètres ont bien été mis à jour"
export const SETTINGS_SUBMIT_FAILED = "Tes paramètres n'ont pas pu être enregistrés. Réessaie dans un instant."
export const OTHER_PAGE_REFUSED = "Une autre page de tes paramètres a été refusée : vérifie-la, puis réessaie."
export const PRIVACY_URL = "https://masteur.com/confidentialite"
export const LEGAL_URL = "https://masteur.com/mentions-legales"
/** Le titre de l'ancienne garde de sortie : celui sous lequel ses boutons sont mesurés. */
const LEAVE_DIALOG_TITLE = "Modifications non enregistrées"

type StoreUrls = { ios: string | null; android: string | null }

/** La fiche de l'app dans son store, ouverte sur l'avis — `SettingsConfigurationScreen.js`. */
function rateAppUrl(storeUrls: StoreUrls): string | null {
    const expo = Constants.expoConfig as { ios?: { appStoreUrl?: string }; android?: { playStoreUrl?: string } } | null
    if (Platform.OS === "ios") {
        const url = storeUrls.ios ?? expo?.ios?.appStoreUrl
        return url ? `${url}?action=write-review` : null
    }
    const url = storeUrls.android ?? expo?.android?.playStoreUrl
    return url ? `${url}&showAllReviews=true` : null
}

/** « Noter l'application » : mobile seulement, quand la fiche du store se connaît. */
export function rateAppAction(storeUrls: StoreUrls): (() => void) | undefined {
    const url = Platform.OS === "web" ? null : rateAppUrl(storeUrls)
    return url != null ? () => void Linking.openURL(url).catch(() => {}) : undefined
}

type StackNavigation = { isFocused: () => boolean }

export function useSettingsSectionShell<S extends string, D extends SettingsDrafts<S>>(args: {
    ui: SettingsUiState<S, D>
    dispatchUi: Dispatch<SettingsUiAction<S>>
    /** La page ouverte a des modifications non enregistrées. */
    dirty: boolean
    loading: boolean
    submitting: boolean
}) {
    const { ui, dispatchUi, dirty, loading, submitting } = args
    const navigation = useNavigation() as unknown as StackNavigation
    const exit = useBackOr("account")

    /** Le retour : la garde d'abord, puis la liste ou l'écran d'avant. */
    const back = useCallback(() => {
        if (ui.view === "hub") {
            exit()
            return
        }
        const target = ui.enteredDirectly ? "exit" : "hub"
        if (dirty) {
            dispatchUi({ type: "ASK_LEAVE", target })
            return
        }
        if (target === "exit") exit()
        else dispatchUi({ type: "LEAVE_SECTION" })
    }, [ui.view, ui.enteredDirectly, dirty, exit, dispatchUi])

    const confirmLeave = useCallback(() => {
        const target = ui.pendingLeave
        trackDialogButton(LEAVE_DIALOG_TITLE, "Quitter")
        dispatchUi({ type: "LEAVE_SECTION" })
        if (target === "exit") exit()
    }, [ui.pendingLeave, exit, dispatchUi])

    // Le bouton retour d'Android suit le même chemin que la flèche de l'en-tête.
    const backRef = useRef(back)
    backRef.current = back
    const navigationRef = useRef(navigation)
    navigationRef.current = navigation
    useEffect(() => {
        const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
            // Un écran poussé par-dessus (le mot de passe, la fermeture) garde son propre retour.
            if (!navigationRef.current.isFocused()) return false
            backRef.current()
            return true
        })
        return () => subscription.remove()
    }, [])

    return {
        back,
        /** Ce que chaque page reçoit : le retour, l'envoi, l'accusé, la garde. */
        shell: {
            onBack: back,
            loading,
            submitting,
            submitError: ui.submitError,
            savedMessage: ui.savedMessage,
            onDismissSaved: () => dispatchUi({ type: "DISMISS_SAVED" }),
            leaveConfirmVisible: ui.pendingLeave != null,
            onConfirmLeave: confirmLeave,
            onCancelLeave: () => {
                trackDialogButton(LEAVE_DIALOG_TITLE, "Non, continuer")
                dispatchUi({ type: "CANCEL_LEAVE" })
            },
            // Le voile ou le retour ferment sans mesurer : l'ancienne boîte ne mesurait que ses boutons.
            onDismissLeave: () => dispatchUi({ type: "CANCEL_LEAVE" }),
        },
    }
}
